import { createContext, useContext, useState, ReactNode, useRef, useEffect, useCallback } from "react";
import { getPlayableUrl } from "@/lib/audioUtils";

interface Beat {
  id: string;
  title: string;
  producer: string;
  mp3_url?: string;
  wav_url?: string;
  image_url?: string;
}

interface AudioPlayerContextType {
  currentBeat: Beat | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isModalOpen: boolean;
  setCurrentBeat: (beat: Beat | null) => void;
  setIsPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setIsMuted: (muted: boolean) => void;
  setModalOpen: (open: boolean) => void;
  playBeat: (beat: Beat) => void;
  togglePlayPause: () => void;
  closeBeat: () => void;
  playNext: () => void;
  playPrevious: () => void;
  setBeats: (beats: Beat[]) => void;
  handleSeek: (time: number) => void;
  handleVolumeChange: (volume: number) => void;
  toggleMute: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error("useAudioPlayer must be used within AudioPlayerProvider");
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export const AudioPlayerProvider = ({ children }: AudioPlayerProviderProps) => {
  const [currentBeat, setCurrentBeat] = useState<Beat | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [beats, setBeats] = useState<Beat[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(75);
  const [isMuted, setIsMuted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element with failsafe
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume / 100;
      
      const audio = audioRef.current;
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };
      
      const handleEnded = () => {
        playNext();
      };
      
      // Failsafe: pause other audio elements
      const handlePlay = () => {
        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach(el => {
          if (el !== audio && !el.paused) {
            el.pause();
            el.currentTime = 0;
          }
        });
      };
      
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
      };
    }
  }, []);

  const [audioReady, setAudioReady] = useState(false);

  // Handle play/pause state changes - only play when audio is ready
  useEffect(() => {
    if (audioRef.current && audioReady) {
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          if (import.meta.env.DEV) console.error('Play error:', err);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, audioReady]);

  // Track if we should auto-play when audio becomes ready
  const pendingPlayRef = useRef(false);

  // Handle beat changes - convert GCS URLs to playable signed URLs
  useEffect(() => {
    let cancelled = false;
    
    const loadAudio = async () => {
      if (!audioRef.current || !currentBeat) return;
      
      const rawUrl = currentBeat.mp3_url || currentBeat.wav_url;
      if (!rawUrl) return;
      
      // Mark audio as not ready while loading
      setAudioReady(false);
      pendingPlayRef.current = isPlaying;
      
      // Get playable URL (converts gcs:// to signed https://)
      const playableUrl = await getPlayableUrl(rawUrl);
      
      if (cancelled || !playableUrl) return;
      
      const audio = audioRef.current;
      audio.src = playableUrl;
      audio.load();
      
      // Wait for audio to be ready; the isPlaying/audioReady effect will start playback
      const handleCanPlay = () => {
        if (!cancelled) {
          setAudioReady(true);
        }
        audio.removeEventListener('canplay', handleCanPlay);
      };
      audio.addEventListener('canplay', handleCanPlay);
    };
    
    loadAudio();
    
    return () => {
      cancelled = true;
    };
  }, [currentBeat]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  const playBeat = (beat: Beat) => {
    if (currentBeat?.id === beat.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentBeat(beat);
      setIsPlaying(true);
    }
    if (import.meta.env.DEV) console.log("Play beat");
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const closeBeat = () => {
    setCurrentBeat(null);
    setIsPlaying(false);
  };

  const playNext = () => {
    if (!currentBeat || beats.length === 0) return;
    
    const currentIndex = beats.findIndex(beat => beat.id === currentBeat.id);
    const nextIndex = (currentIndex + 1) % beats.length;
    const nextBeat = beats[nextIndex];
    
    setCurrentBeat(nextBeat);
    setIsPlaying(true);
  };

  const playPrevious = () => {
    if (!currentBeat || beats.length === 0) return;
    
    const currentIndex = beats.findIndex(beat => beat.id === currentBeat.id);
    const previousIndex = currentIndex === 0 ? beats.length - 1 : currentIndex - 1;
    const previousBeat = beats[previousIndex];
    
    setCurrentBeat(previousBeat);
    setIsPlaying(true);
  };

  const handleSeek = (time: number) => {
    if (audioRef.current && !isNaN(time) && isFinite(time) && duration > 0) {
      // Stop any existing seek operations
      audioRef.current.pause();
      
      // Ensure we don't seek beyond the duration
      const seekTime = Math.max(0, Math.min(time, duration));
      
      // Perform seek
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
      
      // Resume playing if it was playing
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          if (import.meta.env.DEV) console.error(err);
        });
      }
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const value = {
    currentBeat,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isModalOpen,
    setCurrentBeat,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setVolume,
    setIsMuted,
    setModalOpen: setIsModalOpen,
    playBeat,
    togglePlayPause,
    closeBeat,
    playNext,
    playPrevious,
    setBeats,
    handleSeek,
    handleVolumeChange,
    toggleMute,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};